// src/components/admin/ProductDialog.jsx
import React, { useState, useEffect } from 'react';
import { X, Upload, Image as ImageIcon } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { translations } from '../../utils/translations';
import { categoryApi, unitApi, uploadApi } from '../../api';

const ProductDialog = ({ product, onClose, onSubmit }) => {
  const { language } = useApp();
  const t = translations[language];

  const [formData, setFormData] = useState({
    barcode: '', name: '', name_en: '', name_km: '',
    category_id: '', unit_id: '',
    cost_price: '', sale_price: '',
    vip_price: '',
    box_quantity: '',
    box_price: '',
    vip_box_price: '',
    stock: '', min_stock: '', image: '',
    sort_order: '',
    show_in_pos: true,
    show_in_h5: true,
    show_in_transfer: true,
  });

  const [categoryTree, setCategoryTree] = useState([]);
  const [selectedParent, setSelectedParent] = useState('');
  const [units, setUnits] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadCategories();
    loadUnits();
    if (product) {
      setFormData({
        barcode: product.barcode || '',
        name: product.name || '',
        name_en: product.name_en || '',
        name_km: product.name_km || '',
        category_id: product.category_id || '',
        unit_id: product.unit_id || '',
        cost_price: product.cost_price || '',
        sale_price: product.sale_price || '',
        vip_price: product.vip_price || '',
        box_quantity: product.box_quantity || '',
        box_price: product.box_price || '',
        vip_box_price: product.vip_box_price || '',
        stock: product.stock || '',
        min_stock: product.min_stock || '',
        image: product.image || '',
        sort_order: product.sort_order ?? '',
        show_in_pos: product.show_in_pos != null ? !!product.show_in_pos : true,
        show_in_h5: product.show_in_h5 != null ? !!product.show_in_h5 : true,
        show_in_transfer: product.show_in_transfer != null ? !!product.show_in_transfer : true,
      });
      if (product.image) setImagePreview(product.image);
    }
  }, [product]);

  const loadCategories = async () => {
    try {
      const res = await categoryApi.getList();
      if (res.success) {
        setCategoryTree(res.data || []);
        if (product && product.category_id) {
          for (const parent of (res.data || [])) {
            const found = (parent.children || []).find(c => c.id === parseInt(product.category_id));
            if (found) { setSelectedParent(String(parent.id)); break; }
            if (parent.id === parseInt(product.category_id)) {
              setSelectedParent(String(parent.id)); break;
            }
          }
        }
      }
    } catch (err) { console.error('加载分类失败:', err); }
  };

  const loadUnits = async () => {
    try {
      const res = await unitApi.getList({ is_sellable: 1, status: 'active' });
      if (res.success) setUnits(res.data || []);
    } catch (err) { console.error('加载单位失败:', err); }
  };

  const currentChildren = selectedParent
    ? (categoryTree.find(p => p.id === parseInt(selectedParent))?.children || [])
    : [];

  const handleParentChange = (parentId) => {
    setSelectedParent(parentId);
    setFormData(prev => ({ ...prev, category_id: '' }));
  };

  const getCategoryName = (cat) => {
    if (language === 'en') return cat.name_en || cat.name;
    if (language === 'km') return cat.name_km || cat.name;
    return cat.name;
  };

  const getUnitName = (unit) => {
    if (language === 'en') return unit.name_en || unit.name;
    if (language === 'km') return unit.name_km || unit.name;
    return unit.name;
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('图片大小不能超过 5MB'); return; }
    if (!file.type.startsWith('image/')) { alert('请选择图片文件'); return; }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category_id) { alert('请选择商品分类'); return; }
    if ((formData.box_price || formData.vip_box_price) && !formData.box_quantity) {
      alert('设置箱价时必须填写箱容'); return;
    }

    let imagePath = formData.image;
    if (imageFile) {
      setUploading(true);
      try {
        const uploadRes = await uploadApi.uploadProductImage(imageFile);
        if (uploadRes.success && uploadRes.data?.url) {
          imagePath = uploadRes.data.url;
        } else {
          alert('图片上传失败: ' + (uploadRes.message || '未知错误'));
          setUploading(false); return;
        }
      } catch (err) {
        alert('图片上传失败: ' + err.message);
        setUploading(false); return;
      }
      setUploading(false);
    }

    onSubmit({
      ...formData,
      image: imagePath || '',
      cost_price: parseFloat(formData.cost_price) || 0,
      sale_price: parseFloat(formData.sale_price) || 0,
      vip_price: formData.vip_price ? parseFloat(formData.vip_price) : null,
      box_quantity: formData.box_quantity ? parseInt(formData.box_quantity) : null,
      box_price: formData.box_price ? parseFloat(formData.box_price) : null,
      vip_box_price: formData.vip_box_price ? parseFloat(formData.vip_box_price) : null,
      stock: parseInt(formData.stock) || 0,
      min_stock: parseInt(formData.min_stock) || 0,
      sort_order: formData.sort_order !== '' ? parseInt(formData.sort_order) : null,
      show_in_pos: formData.show_in_pos ? 1 : 0,
      show_in_h5: formData.show_in_h5 ? 1 : 0,
      show_in_transfer: formData.show_in_transfer ? 1 : 0,
    });
  };

  // 开关组件
  const Toggle = ({ label, desc, value, onChange }) => (
    <div className="flex items-center justify-between py-2">
      <div>
        <p className="text-sm font-medium text-gray-700">{label}</p>
        {desc && <p className="text-xs text-gray-400 mt-0.5">{desc}</p>}
      </div>
      <button type="button" onClick={() => onChange(!value)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
          value ? 'bg-blue-600' : 'bg-gray-300'
        }`}>
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          value ? 'translate-x-6' : 'translate-x-1'
        }`} />
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
          <h3 className="text-xl font-bold">{product ? '编辑商品' : '添加商品'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">

          {/* 商品图片 */}
          <div>
            <label className="block text-sm font-medium mb-2">商品图片</label>
            <div className="flex items-start space-x-4">
              <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                {imagePreview
                  ? <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  : <ImageIcon className="w-12 h-12 text-gray-400" />}
              </div>
              <div className="flex-1">
                <input type="file" id="imageUpload" accept="image/*" onChange={handleImageChange} className="hidden" />
                <label htmlFor="imageUpload" className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer">
                  <Upload className="w-5 h-5 mr-2" />上传图片
                </label>
                <p className="text-xs text-gray-500 mt-2">支持 JPG、PNG，最大 5MB</p>
              </div>
            </div>
          </div>

          {/* 条码 + 分类 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">条码</label>
              <input type="text" value={formData.barcode}
                onChange={e => setFormData({ ...formData, barcode: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="输入条码" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">大类 *</label>
              <select value={selectedParent} onChange={e => handleParentChange(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" required>
                <option value="">选择大类</option>
                {categoryTree.map(parent => (
                  <option key={parent.id} value={parent.id}>{getCategoryName(parent)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">子分类 *</label>
              <select value={formData.category_id}
                onChange={e => setFormData({ ...formData, category_id: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required disabled={!selectedParent}>
                <option value="">{selectedParent ? '选择子分类' : '请先选大类'}</option>
                {currentChildren.map(child => (
                  <option key={child.id} value={child.id}>{getCategoryName(child)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* 商品名称三语 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">商品名称（中文）*</label>
              <input type="text" value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">商品名称（英文）</label>
              <input type="text" value={formData.name_en}
                onChange={e => setFormData({ ...formData, name_en: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">商品名称（高棉语）</label>
              <input type="text" value={formData.name_km}
                onChange={e => setFormData({ ...formData, name_km: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          {/* 单位 + 成本价 + 零售价 + VIP散装价 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">单位 *</label>
              <select value={formData.unit_id} onChange={e => setFormData({ ...formData, unit_id: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" required>
                <option value="">选择单位</option>
                {units.map(unit => <option key={unit.id} value={unit.id}>{getUnitName(unit)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">成本价 *</label>
              <input type="number" step="0.01" min="0" value={formData.cost_price}
                onChange={e => setFormData({ ...formData, cost_price: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">零售单价 *</label>
              <input type="number" step="0.01" min="0" value={formData.sale_price}
                onChange={e => setFormData({ ...formData, sale_price: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                VIP单价<span className="ml-1 text-xs text-gray-400">（散装）</span>
              </label>
              <input type="number" step="0.01" min="0" value={formData.vip_price}
                onChange={e => setFormData({ ...formData, vip_price: e.target.value })}
                placeholder="不填则无VIP价"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          {/* 箱装设置 */}
          <div className="border border-orange-200 rounded-xl p-4 bg-orange-50">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-lg">📦</span>
              <h4 className="font-semibold text-gray-800">箱装设置</h4>
              <span className="text-xs text-gray-500">（选填，不填则该商品无箱装）</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">箱容<span className="ml-1 text-xs text-gray-400">（1箱=几个）</span></label>
                <input type="number" min="1" step="1" value={formData.box_quantity}
                  onChange={e => setFormData({ ...formData, box_quantity: e.target.value })}
                  placeholder="例：24"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-400" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">整箱价<span className="ml-1 text-xs text-gray-400">（普通客户）</span></label>
                <input type="number" step="0.01" min="0" value={formData.box_price}
                  onChange={e => setFormData({ ...formData, box_price: e.target.value })}
                  placeholder="例：18.00"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-400" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">VIP箱价<span className="ml-1 text-xs text-gray-400">（VIP客户）</span></label>
                <input type="number" step="0.01" min="0" value={formData.vip_box_price}
                  onChange={e => setFormData({ ...formData, vip_box_price: e.target.value })}
                  placeholder="例：16.00"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-400" />
              </div>
            </div>
            {formData.box_quantity && formData.sale_price && (
              <div className="mt-3 text-xs text-gray-500 flex gap-4 flex-wrap">
                <span>📌 箱容：1箱 = {formData.box_quantity} 个</span>
                {formData.box_price && (
                  <span>普通箱价：${parseFloat(formData.box_price).toFixed(2)} / 箱
                    （折合 ${(parseFloat(formData.box_price) / parseInt(formData.box_quantity)).toFixed(3)} / 个）
                  </span>
                )}
                {formData.vip_box_price && (
                  <span>VIP箱价：${parseFloat(formData.vip_box_price).toFixed(2)} / 箱
                    （折合 ${(parseFloat(formData.vip_box_price) / parseInt(formData.box_quantity)).toFixed(3)} / 个）
                  </span>
                )}
              </div>
            )}
          </div>

          {/* 库存 + 最低库存 + 排序权重 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                库存（个）{!product && '*'}
                {product && <span className="ml-1 text-xs text-orange-500">（请用调整库存功能修改）</span>}
              </label>
              <input type="number" min="0" value={formData.stock}
                onChange={e => !product && setFormData({ ...formData, stock: e.target.value })}
                readOnly={!!product}
                className={`w-full px-3 py-2 border rounded-lg ${
                  product ? 'bg-gray-100 cursor-not-allowed text-gray-500' : 'focus:ring-2 focus:ring-blue-500'
                }`}
                required={!product} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">最低库存（个）</label>
              <input type="number" min="0" value={formData.min_stock}
                onChange={e => setFormData({ ...formData, min_stock: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                排序权重<span className="ml-1 text-xs text-gray-400">（数字越小越靠前）</span>
              </label>
              <input type="number" min="0" step="1" value={formData.sort_order}
                onChange={e => setFormData({ ...formData, sort_order: e.target.value })}
                placeholder="例：1、10、100"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          {/* 显示开关 */}
          <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
            <h4 className="font-semibold text-gray-800 mb-2">显示设置</h4>
            <div className="divide-y divide-gray-200">
              <Toggle
                label="显示在收银台"
                desc="关闭后该商品不会出现在收银台商品列表"
                value={formData.show_in_pos}
                onChange={v => setFormData({ ...formData, show_in_pos: v })}
              />
              <Toggle
                label="显示在H5网店"
                desc="关闭后该商品不会出现在H5商城"
                value={formData.show_in_h5}
                onChange={v => setFormData({ ...formData, show_in_h5: v })}
              />
              <Toggle
                label="显示在分店调拨"
                desc="关闭后分店在申请调货时看不到该商品"
                value={formData.show_in_transfer}
                onChange={v => setFormData({ ...formData, show_in_transfer: v })}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button type="button" onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">{t.cancel}</button>
            <button type="submit" disabled={uploading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {uploading ? '上传中...' : product ? t.save : '添加'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductDialog;